/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, Card, Stack } from '@mui/material';
import React from 'react';
import { useResizable } from 'react-resizable-layout';
import { ArrowDropDownDown } from '@opengeoweb/theme';

export interface ResizablePanelProps {
  name: string;
  /** Minimum expanded width, or height in vertical mode. */
  minWidth?: number;
  maxWidth?: number;
  isOpen?: boolean;
  headerAction?: React.ReactElement;
  handleButtonComponent?: React.ReactElement;
  children?: React.ReactNode;
  onSetIsOpen?: (isOpen: boolean) => void;
  onSetCustomWidth?: (width: number) => void;
  onResizeStart?: () => void;
  onResizeEnd?: () => void;
  customWidth?: number;
  /** Size allocated by the parent layout, including temporary growth. */
  initialSize?: number;
  allowOverflow?: boolean;
  isDisplayed?: boolean;
  resizeOnViewCloseSignal?: number;
  /**
   * Direction in which the panel is resized. In 'vertical' mode the panel is
   * resized by height; minWidth/maxWidth/customWidth then apply to the height.
   */
  direction?: 'horizontal' | 'vertical';
}

export const PANEL_COLLAPSED_SIZE = 24;

export const ResizablePanel = React.forwardRef<
  HTMLDivElement,
  ResizablePanelProps
>(
  (
    {
      name,
      minWidth: minWidthProp,
      maxWidth,
      isOpen,
      headerAction,
      handleButtonComponent,
      children,
      onSetIsOpen,
      onSetCustomWidth,
      onResizeStart,
      onResizeEnd,
      customWidth: customWidthProp,
      initialSize,
      allowOverflow = false,
      isDisplayed,
      resizeOnViewCloseSignal,
      direction = 'horizontal',
    }: ResizablePanelProps,
    contentRef,
  ): React.JSX.Element => {
    const customWidth = initialSize ?? customWidthProp;
    const isVertical = direction === 'vertical';
    const openMin = Math.max(
      PANEL_COLLAPSED_SIZE,
      minWidthProp ?? PANEL_COLLAPSED_SIZE,
    );
    const sizeKey = isVertical ? 'height' : 'width';
    const containerRef = React.useRef<HTMLDivElement>(null);
    const widthRef = React.useRef<number | null>(null);
    const previousCustomWidthRef = React.useRef(customWidth);
    const isDraggingRef = React.useRef(false);
    const [autoSizing, setAutoSizing] = React.useState(false);
    const [growing, setGrowing] = React.useState(false);
    const growBasisRef = React.useRef<number>(openMin);
    const previousResizeSignalRef = React.useRef(resizeOnViewCloseSignal);

    const {
      position: containerWidth,
      setPosition: setContainerWidth,
      separatorProps,
    } = useResizable({
      containerRef: containerRef as React.RefObject<HTMLDivElement>,
      axis: isVertical ? 'y' : 'x',
      min: PANEL_COLLAPSED_SIZE,
      max: maxWidth || undefined,
      initial: customWidth ?? openMin,
      onResizeStart: () => {
        isDraggingRef.current = true;
        setAutoSizing(false);
        onResizeStart?.();
      },
      onResizeEnd: ({ position }) => {
        isDraggingRef.current = false;
        if (position <= PANEL_COLLAPSED_SIZE) {
          widthRef.current = null;
          onSetIsOpen?.(false);
        } else {
          widthRef.current = Math.max(position, openMin);
          onSetIsOpen?.(true);
          onSetCustomWidth?.(Math.max(position, openMin));
        }
        // Fire after the open/close calls so consumers tracking the drag
        // lifecycle (e.g. a "resizing" flag) reset only once the close above
        // has been handled.
        onResizeEnd?.();
      },
    });

    const openPanel = (): void => {
      const targetWidth = widthRef.current ?? customWidth ?? null;
      if (targetWidth != null) {
        setAutoSizing(false);
        setContainerWidth(Math.max(targetWidth, openMin));
        onSetIsOpen?.(true);
        return;
      }
      setAutoSizing(true);
      onSetIsOpen?.(true);
    };

    const closePanel = React.useCallback(() => {
      setContainerWidth(PANEL_COLLAPSED_SIZE);
      onSetIsOpen?.(false);
      setAutoSizing(false);
    }, [setContainerWidth, onSetIsOpen]);

    React.useEffect(() => {
      if (previousCustomWidthRef.current !== customWidth) {
        previousCustomWidthRef.current = customWidth;
        widthRef.current = customWidth ?? null;
      }
      // While dragging, useResizable owns containerWidth based on the pointer.
      // Running the width-syncing branches here would jump away from the user's
      // drag position, so skip this effect until the drag ends.
      if (isDraggingRef.current) {
        return;
      }
      if (isOpen) {
        const targetWidth = widthRef.current ?? customWidth ?? null;
        if (targetWidth != null) {
          setAutoSizing(false);
          setContainerWidth(Math.max(targetWidth, openMin));
        } else {
          setAutoSizing(true);
        }
      } else {
        setContainerWidth(PANEL_COLLAPSED_SIZE);
        setAutoSizing(false);
      }
    }, [isOpen, openMin, setContainerWidth, customWidth]);

    // Flip open/closed state during an active drag as the width crosses the
    // collapsed-size threshold, so the panel can close and re-open within a
    // single drag gesture without waiting for pointer release.
    React.useEffect(() => {
      if (!isDraggingRef.current) {
        return;
      }
      if (isOpen && containerWidth <= PANEL_COLLAPSED_SIZE) {
        onSetIsOpen?.(false);
      } else if (!isOpen && containerWidth > PANEL_COLLAPSED_SIZE) {
        onSetIsOpen?.(true);
      }
    }, [isOpen, containerWidth, onSetIsOpen]);

    React.useEffect(() => {
      const viewCloseSignalChanged =
        previousResizeSignalRef.current !== resizeOnViewCloseSignal;
      if (!viewCloseSignalChanged) {
        return;
      }
      previousResizeSignalRef.current = resizeOnViewCloseSignal;

      const panelCanReclaimFreedSpace =
        isOpen && !autoSizing && !isDraggingRef.current;
      if (panelCanReclaimFreedSpace) {
        growBasisRef.current = Math.max(containerWidth, openMin);
        setGrowing(true);
      }
    }, [resizeOnViewCloseSignal, isOpen, autoSizing, containerWidth, openMin]);

    React.useLayoutEffect(() => {
      if (autoSizing) {
        return;
      }
      if (isDraggingRef.current) {
        return;
      }
      if (!isOpen && containerWidth > PANEL_COLLAPSED_SIZE) {
        setContainerWidth(PANEL_COLLAPSED_SIZE);
        return;
      }
      if (isOpen && containerWidth <= PANEL_COLLAPSED_SIZE) {
        setAutoSizing(true);
      }
    }, [autoSizing, containerWidth, isOpen, setContainerWidth]);

    React.useLayoutEffect(() => {
      if (!autoSizing || !containerRef.current) {
        return;
      }
      let raf1 = 0;
      let raf2 = 0;

      const measureContainerWidth = (): void => {
        if (!containerRef.current) {
          return;
        }
        const rect = containerRef.current.getBoundingClientRect();
        const calculatedWidth = Math.max(Math.round(rect[sizeKey]), openMin);
        setContainerWidth(calculatedWidth);
        widthRef.current = calculatedWidth;
        setAutoSizing(false);
      };

      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(measureContainerWidth);
      });

      // eslint-disable-next-line consistent-return
      return (): void => {
        cancelAnimationFrame(raf1);
        cancelAnimationFrame(raf2);
      };
    }, [autoSizing, openMin, setContainerWidth, sizeKey]);

    React.useLayoutEffect(() => {
      if (!growing || !containerRef.current) {
        return;
      }
      let growRaf1 = 0;
      let growRaf2 = 0;

      const lockGrownWidth = (): void => {
        if (!containerRef.current) {
          return;
        }
        const rect = containerRef.current.getBoundingClientRect();
        const lockedWidth = Math.max(Math.round(rect[sizeKey]), openMin);
        setContainerWidth(lockedWidth);
        widthRef.current = lockedWidth;
        setGrowing(false);
      };

      growRaf1 = requestAnimationFrame(() => {
        growRaf2 = requestAnimationFrame(lockGrownWidth);
      });

      // eslint-disable-next-line consistent-return
      return (): void => {
        cancelAnimationFrame(growRaf1);
        cancelAnimationFrame(growRaf2);
      };
    }, [growing, openMin, setContainerWidth, sizeKey]);

    const sizeProps = (
      size: number | string,
      min: number,
    ): Record<string, unknown> =>
      isVertical
        ? {
            width: '100%',
            height: size,
            minHeight: min,
            maxHeight: maxWidth || (allowOverflow ? undefined : '100%'),
          }
        : {
            width: size,
            minWidth: min,
            maxWidth: maxWidth || (allowOverflow ? undefined : '100%'),
          };
    const cardStyleWhenAuto = {
      display: isDisplayed ? 'block' : 'none',
      flex: '1 1 100%',
      boxSizing: 'border-box',
      ...sizeProps('auto', isOpen ? openMin : PANEL_COLLAPSED_SIZE),
    };
    const cardStyleWhenLocked = {
      display: isDisplayed ? 'block' : 'none',
      flex: allowOverflow ? '0 0 auto' : '0 1 auto',
      boxSizing: 'border-box',
      ...sizeProps(
        isOpen ? Math.max(containerWidth, openMin) : containerWidth,
        isOpen ? openMin : PANEL_COLLAPSED_SIZE,
      ),
    };
    const cardStyleWhenGrowing = {
      display: isDisplayed ? 'block' : 'none',
      flex: `${growBasisRef.current} 1 0px`,
      boxSizing: 'border-box',
      ...sizeProps('auto', isOpen ? openMin : PANEL_COLLAPSED_SIZE),
    };

    const getActiveCardStyle = (): Record<string, unknown> => {
      if (growing) {
        return cardStyleWhenGrowing;
      }
      if (autoSizing) {
        return cardStyleWhenAuto;
      }
      return cardStyleWhenLocked;
    };

    return (
      <Card
        className="sortable-item"
        data-autosizing={autoSizing ? 'true' : 'false'}
        data-growing={growing ? 'true' : 'false'}
        data-testid="resizable-panel"
        ref={containerRef}
        sx={{
          ...(isVertical ? {} : { height: '100%' }),
          position: 'relative',
          ...getActiveCardStyle(),
        }}
      >
        <Box ref={contentRef} sx={{ height: '100%', width: '100%' }}>
          {!isOpen && isVertical ? (
            <Box
              data-testid="closed-panel-header"
              onClick={openPanel}
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              <Box
                onClick={(event): void => event.stopPropagation()}
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                {handleButtonComponent}
              </Box>
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  paddingRight: '10px',
                  overflow: 'hidden',
                  marginTop: 0,
                }}
              >
                <Box
                  sx={{
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    marginLeft: '16px',
                    paddingTop: 0,
                  }}
                >
                  {name}
                </Box>
                <ArrowDropDownDown sx={{ transform: 'rotate(180deg)' }} />
              </Box>
            </Box>
          ) : null}
          {!isOpen && !isVertical && (
            <Box
              data-testid="closed-panel-header"
              onClick={openPanel}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                writingMode: 'vertical-lr',
                transform: 'rotate(180deg)',
                paddingBottom: '10px',
                cursor: 'pointer',
              }}
            >
              {name}
              <ArrowDropDownDown sx={{ transform: 'rotate(90deg)' }} />
              <Box onClick={(event): void => event.stopPropagation()}>
                {handleButtonComponent}
              </Box>
            </Box>
          )}
          {!!isOpen && (
            <Box
              data-testid="open-panel-header"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
              }}
            >
              <Stack sx={{ height: '100%' }}>
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    flex: '0 0 auto',
                  }}
                >
                  <Box
                    onClick={(event): void => event.stopPropagation()}
                    sx={{ paddingTop: '8px' }}
                  >
                    {handleButtonComponent}
                  </Box>
                  <Box
                    onClick={closePanel}
                    sx={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      paddingRight: '10px',
                      overflow: 'hidden',
                      marginTop: '4px',
                      cursor: 'pointer',
                    }}
                  >
                    <Box onClick={(event): void => event.stopPropagation()}>
                      {!!headerAction && headerAction}
                    </Box>
                    <Box
                      sx={{
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        marginLeft: '16px',
                        height: '100%',
                        paddingTop: '3px',
                      }}
                    >
                      {name}
                    </Box>
                    <ArrowDropDownDown />
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'stretch',
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden',
                  }}
                >
                  {children}
                </Box>
              </Stack>
            </Box>
          )}
          <Box
            data-testid="resizable-panel-drag-handle"
            {...separatorProps}
            sx={{
              position: 'absolute',
              ...(isVertical
                ? {
                    left: 0,
                    bottom: 0,
                    width: '100%',
                    height: '5px',
                    cursor: 'row-resize',
                  }
                : {
                    right: 0,
                    top: 0,
                    height: '100%',
                    width: '5px',
                    cursor: 'col-resize',
                  }),
            }}
          />
        </Box>
      </Card>
    );
  },
);

ResizablePanel.displayName = 'ResizablePanel';
